import React, { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import API from '../api';
import Modal from './Modal';
import { slugify, sanitizeSlugInput } from '../utils/slug';

const EMPTY = { name: '', slug: '', description: '', image_url: '', sort_order: 0, is_active: 1 };
const BASE = process.env.REACT_APP_API_URL?.replace('/api', '') ?? 'http://localhost:5000';

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => API.get('/categories/all').then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    const nextOrder = items.length ? Math.max(...items.map(i => i.sort_order)) + 1 : 0;
    setForm({ ...EMPTY, sort_order: nextOrder });
    setModal({ mode: 'add' });
  };
  const openEdit = (item) => { setForm({ ...item }); setModal({ mode: 'edit', id: item.id }); };

  const handleNameChange = (name) => {
    setForm(f => ({ ...f, name, slug: modal?.mode === 'add' ? slugify(name) : f.slug }));
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await API.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, image_url: data.url }));
    } catch { alert('Upload failed'); }
    setUploading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modal.mode === 'add') await API.post('/categories', form);
      else await API.put(`/categories/${modal.id}`, form);
      setModal(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category? Products in it will lose their category.')) return;
    await API.delete(`/categories/${id}`); load();
  };

  const imgSrc = (url) => url?.startsWith('/') ? `${BASE}${url}` : url;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-primary-dark">Product Categories</h1>
          <p className="text-sm text-gray-500 mt-1">Organise your products into categories</p>
        </div>
        <button onClick={openAdd} className="btn-primary text-sm flex items-center gap-2">
          <PlusIcon className="w-4 h-4" /> Add Category
        </button>
      </div>

      <div className="bg-white border border-gray-100 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              {['#', 'Image', 'Name', 'Slug', 'Status', 'Order', 'Views', ''].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">No categories yet.</td></tr>
            )}
            {items.map((item, i) => (
              <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                <td className="px-4 py-3 text-gray-400 text-xs">{i + 1}</td>
                <td className="px-4 py-3">
                  <div className="w-12 h-9 bg-gray-100 overflow-hidden">
                    {item.image_url
                      ? <img src={imgSrc(item.image_url)} alt={item.name} className="w-full h-full object-cover" />
                      : <div className="w-full h-full bg-primary/10" />}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-800">{item.name}</div>
                  {item.description && <div className="text-xs text-gray-400 truncate max-w-xs">{item.description}</div>}
                </td>
                <td className="px-4 py-3 text-gray-400 font-mono text-xs">{item.slug}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-medium px-2 py-0.5 ${item.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {item.is_active ? 'Active' : 'Hidden'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500">{item.sort_order}</td>
                <td className="px-4 py-3 text-gray-500">{item.unique_view_count ?? 0} <span className="text-gray-400 text-xs">({item.view_count ?? 0})</span></td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(item)} className="p-1.5 text-gray-400 hover:text-primary border border-gray-200 hover:border-primary transition-colors">
                      <PencilIcon className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(item.id)} className="p-1.5 text-gray-400 hover:text-red-500 border border-gray-200 hover:border-red-300 transition-colors">
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={modal.mode === 'add' ? 'Add Category' : 'Edit Category'} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Name *</label>
              <input value={form.name} onChange={e => handleNameChange(e.target.value)}
                className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Slug *</label>
              <input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: sanitizeSlugInput(e.target.value) }))}
                className="w-full border border-gray-200 px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary" />
              <p className="text-xs text-gray-400 mt-1">Used in the URL. Auto-generated from name.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Description</label>
              <textarea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={3} className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary resize-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Banner Image</label>
              {form.image_url && <img src={imgSrc(form.image_url)} alt="preview" className="h-28 w-full object-cover border border-gray-200 mb-2" />}
              <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading}
                className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-4 file:border-0 file:bg-primary file:text-white file:text-xs file:cursor-pointer mb-2" />
              <input value={form.image_url || ''} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                placeholder="or paste image URL..." className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Sort Order</label>
                <input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: +e.target.value }))}
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Status</label>
                <select value={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: +e.target.value }))}
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-primary">
                  <option value={1}>Active</option>
                  <option value={0}>Hidden</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={handleSave} disabled={saving || !form.name || !form.slug} className="btn-primary text-sm disabled:opacity-60 flex-1">
                {saving ? 'Saving...' : 'Save Category'}
              </button>
              <button onClick={() => setModal(null)} className="btn-outline text-sm flex-1">Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
